/**
 * ثيمات جاهزة + خطوط عربية + أدوات ألوان.
 * الثيم يُخزَّن كـ ThemeTokens في إعدادات المنصة/الشركة/العلامة/الفرع.
 */

import type { ThemeTokens } from '@/domain/types';
import { DEFAULT_THEME } from '@/domain/settings';

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** تدرّج صالح = مصفوفة من 2 إلى 4 ألوان hex فقط. */
function sanitizeGradient(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const stops = value
    .filter((stop): stop is string => typeof stop === 'string' && HEX_COLOR.test(stop))
    .slice(0, 4);
  return stops.length >= 2 ? stops : undefined;
}

/** يمنع تمرير قيم CSS غير متوقعة من إعدادات قابلة للتعديل. */
export function sanitizeThemeTokens(input: Partial<ThemeTokens> | null | undefined): ThemeTokens {
  const isHex = (value: unknown): value is string =>
    typeof value === 'string' && HEX_COLOR.test(value);
  const font =
    typeof input?.fontFamily === 'string' && input.fontFamily.trim().length > 0
      ? input.fontFamily.slice(0, 200)
      : DEFAULT_THEME.fontFamily;
  return {
    primary: isHex(input?.primary) ? input.primary : DEFAULT_THEME.primary,
    accent: isHex(input?.accent) ? input.accent : DEFAULT_THEME.accent,
    radius: input?.radius === 'sm' || input?.radius === 'lg' ? input.radius : 'md',
    mode:
      input?.mode === 'light' || input?.mode === 'dark' || input?.mode === 'system'
        ? input.mode
        : 'system',
    fontFamily: font,
    logoAttachmentId: input?.logoAttachmentId ?? null,
    logoDataUrl:
      typeof input?.logoDataUrl === 'string' &&
      input.logoDataUrl.startsWith('data:image/') &&
      input.logoDataUrl.length <= 500_000
        ? input.logoDataUrl
        : null,
    backgroundLight: isHex(input?.backgroundLight) ? input.backgroundLight : undefined,
    backgroundDark: isHex(input?.backgroundDark) ? input.backgroundDark : undefined,
    surfaceLight: isHex(input?.surfaceLight) ? input.surfaceLight : undefined,
    surfaceDark: isHex(input?.surfaceDark) ? input.surfaceDark : undefined,
    textLight: isHex(input?.textLight) ? input.textLight : undefined,
    textDark: isHex(input?.textDark) ? input.textDark : undefined,
    sidebarTone:
      input?.sidebarTone === 'tinted' ||
      input?.sidebarTone === 'primary' ||
      input?.sidebarTone === 'surface'
        ? input.sidebarTone
        : undefined,
    surfaceStyle:
      input?.surfaceStyle === 'flat' ||
      input?.surfaceStyle === 'outlined' ||
      input?.surfaceStyle === 'glass' ||
      input?.surfaceStyle === 'soft'
        ? input.surfaceStyle
        : undefined,
    bgStyle:
      input?.bgStyle === 'gradient' ||
      input?.bgStyle === 'mesh' ||
      input?.bgStyle === 'plain' ||
      input?.bgStyle === 'spectrum'
        ? input.bgStyle
        : undefined,
    gradientLight: sanitizeGradient(input?.gradientLight),
    gradientDark: sanitizeGradient(input?.gradientDark),
  };
}

export interface ThemePreset {
  id: string;
  name: string;
  /** وصف قصير يظهر أسفل اسم الثيم. */
  hint: string;
  primary: string;
  accent: string;
  radius: ThemeTokens['radius'];
  /** وضع الثيم: 7 نهارية و7 ليلية. */
  mode: ThemeTokens['mode'];
  fontFamily?: string;
  sidebarTone?: ThemeTokens['sidebarTone'];
  surfaceStyle?: ThemeTokens['surfaceStyle'];
  bgStyle?: ThemeTokens['bgStyle'];
  /** عائلة الثيم: كلاسيكي أو خلفية متعددة الألوان. */
  family?: 'classic' | 'gradient';
  /** ألوان تدرّج الخلفية في الوضع الفاتح. */
  gradientLight?: string[];
  /** ألوان تدرّج الخلفية في الوضع الداكن. */
  gradientDark?: string[];
  backgroundLight?: string;
  backgroundDark?: string;
  surfaceLight?: string;
  surfaceDark?: string;
  textLight?: string;
  textDark?: string;
}

/** ألوان التدرّج الافتراضية عند تفعيل النمط المتعدد يدويًا. */
export const DEFAULT_GRADIENT_LIGHT = ['#fde7f3', '#e9e4ff', '#dcf7ef'] as const;
export const DEFAULT_GRADIENT_DARK = ['#101a36', '#171032', '#0a2430'] as const;

/** عائلات الخطوط (نفس القيم في FONT_PRESETS أدناه). */
const FONT_SYSTEM = "'Segoe UI', Tahoma, system-ui, -apple-system, sans-serif";
const FONT_IBM = "'IBM Plex Sans Arabic', 'Segoe UI', Tahoma, sans-serif";
const FONT_CAIRO = "'Cairo', 'Segoe UI', Tahoma, sans-serif";
const FONT_TAJAWAL = "'Tajawal', 'Segoe UI', Tahoma, sans-serif";
const FONT_ALMARAI = "'Almarai', 'Segoe UI', Tahoma, sans-serif";

/**
 * 14 هوية بصرية كاملة: سبع نهارية وسبع ليلية.
 * كل ثيم يغيّر الخلفية والأسطح والحدود والشريط الجانبي والخط والحواف
 * والتدرّج الخلفي، وليس اللون الأساسي فقط.
 */
export const THEME_PRESETS: ThemePreset[] = [
  /* ------------------------------- نهارية ------------------------------- */
  {
    id: 'day-cloud',
    name: 'سحابة',
    hint: 'هادئ ومتوازن',
    primary: '#0f766e',
    accent: '#b45309',
    radius: 'md',
    mode: 'light',
    fontFamily: FONT_SYSTEM,
    sidebarTone: 'surface',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundLight: '#f3f7f7',
    surfaceLight: '#ffffff',
    textLight: '#10201e',
  },
  {
    id: 'day-ocean',
    name: 'محيط',
    hint: 'أزرق منعش',
    primary: '#0369a1',
    accent: '#0e7490',
    radius: 'lg',
    mode: 'light',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundLight: '#eef6fc',
    surfaceLight: '#ffffff',
    textLight: '#0b2239',
  },
  {
    id: 'day-forest',
    name: 'غابة',
    hint: 'أخضر طبيعي',
    primary: '#15803d',
    accent: '#a16207',
    radius: 'md',
    mode: 'light',
    fontFamily: FONT_ALMARAI,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'mesh',
    backgroundLight: '#f2f7f1',
    surfaceLight: '#fdfefb',
    textLight: '#15261a',
  },
  {
    id: 'day-sand',
    name: 'صحراء',
    hint: 'دافئ ترابي',
    primary: '#b45309',
    accent: '#0f766e',
    radius: 'lg',
    mode: 'light',
    fontFamily: FONT_TAJAWAL,
    sidebarTone: 'surface',
    surfaceStyle: 'soft',
    bgStyle: 'mesh',
    backgroundLight: '#faf4e8',
    surfaceLight: '#fffdf6',
    textLight: '#2a1c0d',
  },
  {
    id: 'day-rose',
    name: 'ورد',
    hint: 'وردي أنيق',
    primary: '#be123c',
    accent: '#7c3aed',
    radius: 'lg',
    mode: 'light',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundLight: '#fdf3f6',
    surfaceLight: '#ffffff',
    textLight: '#2a0e18',
  },
  {
    id: 'day-violet',
    name: 'بنفسج',
    hint: 'بنفسجي جريء',
    primary: '#6d28d9',
    accent: '#be185d',
    radius: 'md',
    mode: 'light',
    fontFamily: FONT_IBM,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundLight: '#f6f3fe',
    surfaceLight: '#ffffff',
    textLight: '#1d1236',
  },
  {
    id: 'day-graphite',
    name: 'جرافيت',
    hint: 'رمادي احترافي بشريط داكن',
    primary: '#334155',
    accent: '#b45309',
    radius: 'sm',
    mode: 'light',
    fontFamily: FONT_SYSTEM,
    sidebarTone: 'primary',
    surfaceStyle: 'outlined',
    bgStyle: 'plain',
    backgroundLight: '#f3f4f6',
    surfaceLight: '#ffffff',
    textLight: '#0f172a',
  },

  /* --------------------------- تدرّجات نهارية --------------------------- */
  {
    id: 'day-gradient-aurora',
    name: 'شفق ناعم',
    hint: 'وردي وبنفسجي ونعناعي',
    primary: '#7c3aed',
    accent: '#0d9488',
    radius: 'lg',
    mode: 'light',
    family: 'gradient',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientLight: ['#fde7f3', '#e9e4ff', '#dcf7ef'],
    backgroundLight: '#f7eefb',
    surfaceLight: '#ffffff',
    textLight: '#24102a',
  },
  {
    id: 'day-gradient-lagoon',
    name: 'بحيرة',
    hint: 'سماوي وفيروزي وليموني',
    primary: '#0e7490',
    accent: '#4d7c0f',
    radius: 'md',
    mode: 'light',
    family: 'gradient',
    fontFamily: FONT_ALMARAI,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientLight: ['#ddf6ff', '#d8fbf1', '#edf9d8'],
    backgroundLight: '#e9fbfa',
    surfaceLight: '#fbfffd',
    textLight: '#0a2630',
  },
  {
    id: 'day-gradient-coral',
    name: 'مرجان',
    hint: 'خوخي ووردي وليلكي',
    primary: '#c2410c',
    accent: '#a21caf',
    radius: 'lg',
    mode: 'light',
    family: 'gradient',
    fontFamily: FONT_TAJAWAL,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientLight: ['#fff0e4', '#ffe3ec', '#f1e7ff'],
    backgroundLight: '#fdf0f3',
    surfaceLight: '#ffffff',
    textLight: '#2a1116',
  },

  /* -------------------------------- ليلية -------------------------------- */
  {
    id: 'night-midnight',
    name: 'منتصف الليل',
    hint: 'كحلي عميق',
    primary: '#3b82f6',
    accent: '#a78bfa',
    radius: 'md',
    mode: 'dark',
    fontFamily: FONT_IBM,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundDark: '#050a13',
    surfaceDark: '#0b1420',
    textDark: '#e8eefb',
  },
  {
    id: 'night-emerald',
    name: 'زمردي',
    hint: 'زمردي على أسود',
    primary: '#059669',
    accent: '#d97706',
    radius: 'md',
    mode: 'dark',
    fontFamily: FONT_ALMARAI,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'mesh',
    backgroundDark: '#04100b',
    surfaceDark: '#0a1a13',
    textDark: '#e6f7ef',
  },
  {
    id: 'night-violet',
    name: 'بنفسجي ليلي',
    hint: 'بنفسجي ناعم',
    primary: '#7c3aed',
    accent: '#db2777',
    radius: 'lg',
    mode: 'dark',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundDark: '#0b0714',
    surfaceDark: '#151022',
    textDark: '#f1eafe',
  },
  {
    id: 'night-sunset',
    name: 'غروب داكن',
    hint: 'برتقالي دافئ',
    primary: '#ea580c',
    accent: '#e11d48',
    radius: 'lg',
    mode: 'dark',
    fontFamily: FONT_TAJAWAL,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'mesh',
    backgroundDark: '#120805',
    surfaceDark: '#1d0f0a',
    textDark: '#fdeee6',
  },
  {
    id: 'night-ocean',
    name: 'محيط داكن',
    hint: 'سماوي عميق',
    primary: '#0891b2',
    accent: '#2563eb',
    radius: 'md',
    mode: 'dark',
    fontFamily: FONT_IBM,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'gradient',
    backgroundDark: '#03101a',
    surfaceDark: '#071c2b',
    textDark: '#e2f5fb',
  },
  {
    id: 'night-rose',
    name: 'وردي داكن',
    hint: 'وردي على بلوم',
    primary: '#be185d',
    accent: '#7c3aed',
    radius: 'lg',
    mode: 'dark',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'mesh',
    backgroundDark: '#12060f',
    surfaceDark: '#1e0d19',
    textDark: '#fbe9f3',
  },
  {
    id: 'night-graphite',
    name: 'جرافيت داكن',
    hint: 'رمادي دخاني',
    primary: '#64748b',
    accent: '#d97706',
    radius: 'sm',
    mode: 'dark',
    fontFamily: FONT_SYSTEM,
    sidebarTone: 'surface',
    surfaceStyle: 'outlined',
    bgStyle: 'plain',
    backgroundDark: '#0a0c0f',
    surfaceDark: '#14181d',
    textDark: '#eef2f6',
  },

  /* --------------------------- تدرّجات ليلية --------------------------- */
  {
    id: 'night-gradient-aurora',
    name: 'شفق قطبي',
    hint: 'كحلي وزمردي وبنفسجي',
    primary: '#22d3ee',
    accent: '#a78bfa',
    radius: 'lg',
    mode: 'dark',
    family: 'gradient',
    fontFamily: FONT_IBM,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientDark: ['#061326', '#0a1b38', '#082a33'],
    backgroundDark: '#071427',
    surfaceDark: '#0d1c2e',
    textDark: '#ecf3ff',
  },
  {
    id: 'night-gradient-galaxy',
    name: 'مجرة',
    hint: 'نيلي وأرجواني وتوتي',
    primary: '#a78bfa',
    accent: '#f472b6',
    radius: 'lg',
    mode: 'dark',
    family: 'gradient',
    fontFamily: FONT_CAIRO,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientDark: ['#0c071f', '#160a30', '#260a2b'],
    backgroundDark: '#0d0820',
    surfaceDark: '#150f2a',
    textDark: '#f6eaff',
  },
  {
    id: 'night-gradient-lava',
    name: 'حمم',
    hint: 'فحمي وجمري ونبيذي',
    primary: '#fb923c',
    accent: '#f43f5e',
    radius: 'md',
    mode: 'dark',
    family: 'gradient',
    fontFamily: FONT_TAJAWAL,
    sidebarTone: 'tinted',
    surfaceStyle: 'soft',
    bgStyle: 'spectrum',
    gradientDark: ['#150707', '#240a0c', '#1c0818'],
    backgroundDark: '#170a0b',
    surfaceDark: '#21100f',
    textDark: '#ffeadf',
  },
];

/* ----------------------------- محرّك لوحة الثيم ----------------------------- */

export interface ThemePalette {
  bg: string;
  surface: string;
  surface2: string;
  surface3: string;
  border: string;
  borderStrong: string;
  text: string;
  text2: string;
  muted: string;
  muted2: string;
  primary: string;
  primaryStrong: string;
  primarySoft: string;
  primaryContrast: string;
  accent: string;
  accentSoft: string;
  sidebarBg: string;
  sidebarImage: string;
  sidebarText: string;
  sidebarLink: string;
  sidebarMuted: string;
  sidebarBorder: string;
  sidebarHover: string;
  sidebarActiveBg: string;
  sidebarActiveText: string;
  topbarBg: string;
  bgImage: string;
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
}

/** يمزج لونين: amount = نسبة اللون الثاني (0..1). */
export function mixHex(from: string, to: string, amount: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  if (!a || !b) return from;
  const ratio = Math.min(1, Math.max(0, amount));
  return toHex(
    a.r + (b.r - a.r) * ratio,
    a.g + (b.g - a.g) * ratio,
    a.b + (b.b - a.b) * ratio,
  );
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** أعلى تباين بين الأبيض أو الأزرق الداكن على لون معيّن. */
function readableOn(background: string): string {
  const withWhite = 1.05 / (relativeLuminance(background) + 0.05);
  const withInk = (relativeLuminance(background) + 0.05) / (relativeLuminance('#0b1220') + 0.05);
  return withWhite >= withInk ? '#ffffff' : '#0b1220';
}

/** نسبة تباين WCAG بين لونين. */
export function contrastRatio(first: string, second: string): number {
  const a = relativeLuminance(first);
  const b = relativeLuminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/**
 * يعدّل لون النص تلقائيًا حتى يصل إلى الحد الأدنى للتباين مع خلفيته.
 * يتحرك نحو الأبيض على الخلفيات الداكنة ونحو الأسود على الفاتحة.
 */
function ensureContrast(foreground: string, background: string, minimum = 4.5): string {
  if (!/^#[0-9a-f]{6}$/i.test(foreground) || !/^#[0-9a-f]{6}$/i.test(background)) {
    return foreground;
  }
  if (contrastRatio(foreground, background) >= minimum) return foreground;
  const target = relativeLuminance(background) < 0.35 ? '#ffffff' : '#000000';
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixHex(foreground, target, step / 20);
    if (contrastRatio(candidate, background) >= minimum) return candidate;
  }
  return target;
}

/**
 * يختار لونًا مخصّصًا فقط إذا كان متوافقًا مع الوضع (فاتح/داكن)،
 * وإلا يعود للون الافتراضي. يمنع مثلًا «سطح داكن» فاتحًا جدًا يكسر التباين.
 */
function modeColor(value: string | undefined, fallback: string, shouldBeDark: boolean): string {
  if (!value || !/^#[0-9a-f]{6}$/i.test(value)) return fallback;
  const isDark = relativeLuminance(value) < 0.4;
  return isDark === shouldBeDark ? value : fallback;
}

/**
 * تدرّج آمن للوضع: كل الألوان فاتحة في الوضع النهاري وداكنة في الليلي.
 * أي مجموعة مخالفة تُتجاهل وتُستبدل بخلفية الثيم السادة.
 */
function usableGradient(stops: string[] | undefined, dark: boolean): string[] | null {
  if (!stops || stops.length < 2 || !stops.every((stop) => HEX_COLOR.test(stop))) return null;
  const valid = stops.every((stop) => {
    const value = relativeLuminance(stop);
    return dark ? value <= 0.24 : value >= 0.55;
  });
  return valid ? stops : null;
}

/**
 * يبني كل متغيّرات الواجهة من الألوان الأساسية للثيم.
 * النتيجة متسقة لأي مجموعة ألوان ولا تعتمد على قيم CSS الثابتة.
 */
export function buildThemePalette(tokens: ThemeTokens, mode: 'light' | 'dark'): ThemePalette {
  const dark = mode === 'dark';
  const bg = modeColor(
    dark ? tokens.backgroundDark : tokens.backgroundLight,
    dark ? '#0b1117' : '#f5f6f7',
    dark,
  );
  const surface = modeColor(
    dark ? tokens.surfaceDark : tokens.surfaceLight,
    dark ? '#121a22' : '#ffffff',
    dark,
  );
  const rawText = modeColor(
    dark ? tokens.textDark : tokens.textLight,
    dark ? '#eef2f6' : '#101828',
    !dark,
  );
  const bgStyle = tokens.bgStyle ?? 'plain';
  const gradient =
    bgStyle === 'spectrum'
      ? usableGradient(dark ? tokens.gradientDark : tokens.gradientLight, dark)
      : null;
  let text = ensureContrast(rawText, surface, 7);
  if (contrastRatio(text, bg) < 4.5) text = ensureContrast(text, bg, 4.5);
  if (gradient) {
    for (const stop of gradient) text = ensureContrast(text, stop, 7);
    text = ensureContrast(text, surface, 7);
  }

  const surface2 = mixHex(surface, text, dark ? 0.055 : 0.035);
  const surface3 = mixHex(surface, text, dark ? 0.1 : 0.065);
  const outlined = tokens.surfaceStyle === 'outlined';
  const border = mixHex(surface, text, outlined ? (dark ? 0.26 : 0.2) : dark ? 0.16 : 0.11);
  const borderStrong = mixHex(
    surface,
    text,
    outlined ? (dark ? 0.38 : 0.3) : dark ? 0.26 : 0.18,
  );
  let text2 = ensureContrast(mixHex(text, surface, 0.16), surface, 4.5);
  let muted = ensureContrast(mixHex(text, surface, 0.42), surface, 4.5);
  const muted2 = ensureContrast(mixHex(text, surface, 0.6), surface, 3);

  // لون الخلفية الخفيف = سطح + نسبة صغيرة من اللون (وليس العكس).
  const primarySoft = mixHex(surface, tokens.primary, dark ? 0.22 : 0.11);
  const accentSoft = mixHex(surface, tokens.accent, dark ? 0.18 : 0.12);
  // النص الثانوي يظهر أيضًا داخل الصفوف الملوّنة (مثل الإشعارات غير المقروءة).
  muted = ensureContrast(muted, primarySoft, 4.5);
  if (gradient) {
    for (const stop of gradient) {
      text2 = ensureContrast(text2, stop, 4.5);
      muted = ensureContrast(muted, stop, 4.5);
    }
  }
  let primaryStrong = dark
    ? mixHex(tokens.primary, '#ffffff', 0.32)
    : mixHex(tokens.primary, '#000000', 0.22);
  primaryStrong = ensureContrast(primaryStrong, primarySoft, 4.5);
  primaryStrong = ensureContrast(primaryStrong, surface, 4.5);

  let sidebarBg = surface;
  let sidebarImage = 'none';
  let sidebarText = text;
  let sidebarLink = text2;
  let sidebarMuted = muted;
  let sidebarBorder = border;
  let sidebarHover = surface2;
  let sidebarActiveBg = primarySoft;
  let sidebarActiveText = primaryStrong;

  if (tokens.sidebarTone === 'tinted') {
    sidebarBg = mixHex(surface, tokens.primary, dark ? 0.14 : 0.07);
    sidebarBorder = mixHex(sidebarBg, text, dark ? 0.14 : 0.1);
    sidebarHover = mixHex(sidebarBg, text, dark ? 0.06 : 0.04);
    sidebarActiveBg = mixHex(sidebarBg, tokens.primary, dark ? 0.26 : 0.14);
    sidebarActiveText = dark
      ? mixHex(tokens.primary, '#ffffff', 0.4)
      : mixHex(tokens.primary, '#000000', 0.28);
    sidebarLink = mixHex(text, sidebarBg, 0.16);
    sidebarMuted = mixHex(text, sidebarBg, 0.44);
  } else if (tokens.sidebarTone === 'primary') {
    sidebarBg = mixHex(tokens.primary, dark ? '#05070b' : '#0a1016', 0.68);
    sidebarText = '#ffffff';
    sidebarLink = 'rgba(255, 255, 255, 0.78)';
    sidebarMuted = 'rgba(255, 255, 255, 0.64)';
    sidebarBorder = 'rgba(255, 255, 255, 0.14)';
    sidebarHover = 'rgba(255, 255, 255, 0.09)';
    sidebarActiveBg = 'rgba(255, 255, 255, 0.16)';
    sidebarActiveText = '#ffffff';
  }

  // في الثيمات التدرّجية يمتد التدرّج إلى الشريط الجانبي أيضًا.
  if (gradient) {
    sidebarBg = gradient[0];
    sidebarImage = `linear-gradient(180deg, ${gradient.join(', ')})`;
    sidebarText = text;
    sidebarLink = text2;
    sidebarMuted = muted;
    sidebarBorder = rgba(text, dark ? 0.16 : 0.12);
    sidebarHover = rgba(text, dark ? 0.08 : 0.05);
    sidebarActiveBg = primarySoft;
    sidebarActiveText = primaryStrong;
  }

  sidebarText = ensureContrast(sidebarText, sidebarBg, 7);
  sidebarLink = ensureContrast(sidebarLink, sidebarBg, 4.5);
  sidebarMuted = ensureContrast(sidebarMuted, sidebarBg, 3);
  sidebarActiveText = ensureContrast(sidebarActiveText, sidebarActiveBg, 4.5);

  const glass = tokens.surfaceStyle === 'glass';
  const topbarBg =
    gradient || glass ? rgba(surface, dark ? 0.76 : 0.8) : surface;
  const bgImage = gradient
    ? `linear-gradient(135deg, ${gradient.join(', ')})`
    : bgStyle === 'gradient'
      ? `linear-gradient(180deg, ${rgba(tokens.primary, dark ? 0.16 : 0.08)} 0%, ${rgba(tokens.primary, 0)} 46%)`
      : bgStyle === 'mesh'
        ? `radial-gradient(900px 520px at 88% -8%, ${rgba(tokens.primary, dark ? 0.2 : 0.1)}, transparent 62%), radial-gradient(760px 460px at -12% 8%, ${rgba(tokens.accent, dark ? 0.16 : 0.09)}, transparent 58%)`
        : 'none';

  const shadowColor = '#101828';
  const shadowSm =
    tokens.surfaceStyle === 'flat' || outlined
      ? 'none'
      : `0 1px 2px ${rgba(shadowColor, dark ? 0.4 : 0.06)}`;
  const shadowMd =
    tokens.surfaceStyle === 'flat' || outlined
      ? 'none'
      : `0 4px 16px ${rgba(shadowColor, dark ? 0.35 : 0.08)}`;
  const shadowLg =
    tokens.surfaceStyle === 'flat' || outlined
      ? 'none'
      : `0 16px 40px ${rgba(shadowColor, dark ? 0.5 : 0.14)}`;

  return {
    bg,
    surface,
    surface2,
    surface3,
    border,
    borderStrong,
    text,
    text2,
    muted,
    muted2,
    primary: tokens.primary,
    primaryStrong,
    primarySoft,
    primaryContrast: readableOn(tokens.primary),
    accent: tokens.accent,
    accentSoft,
    sidebarBg,
    sidebarImage,
    sidebarText,
    sidebarLink,
    sidebarMuted,
    sidebarBorder,
    sidebarHover,
    sidebarActiveBg,
    sidebarActiveText,
    topbarBg,
    bgImage,
    shadowSm,
    shadowMd,
    shadowLg,
  };
}

export interface FontPreset {
  id: string;
  name: string;
  /** قيمة CSS الكاملة لـ font-family */
  family: string;
  /** عائلة Google Fonts المطلوبة، إن وُجدت */
  google?: string;
}

export const FONT_PRESETS: FontPreset[] = [
  {
    id: 'system',
    name: 'خط النظام (سريع)',
    family: "'Segoe UI', Tahoma, system-ui, -apple-system, sans-serif",
  },
  {
    id: 'ibm',
    name: 'IBM Plex Sans Arabic',
    family: "'IBM Plex Sans Arabic', 'Segoe UI', Tahoma, sans-serif",
    google: 'IBM+Plex+Sans+Arabic:wght@400;500;600;700',
  },
  {
    id: 'cairo',
    name: 'Cairo',
    family: "'Cairo', 'Segoe UI', Tahoma, sans-serif",
    google: 'Cairo:wght@400;500;600;700',
  },
  {
    id: 'tajawal',
    name: 'Tajawal',
    family: "'Tajawal', 'Segoe UI', Tahoma, sans-serif",
    google: 'Tajawal:wght@400;500;700',
  },
  {
    id: 'almarai',
    name: 'Almarai',
    family: "'Almarai', 'Segoe UI', Tahoma, sans-serif",
    google: 'Almarai:wght@400;700;800',
  },
  {
    id: 'kufi',
    name: 'Noto Kufi Arabic',
    family: "'Noto Kufi Arabic', 'Segoe UI', Tahoma, sans-serif",
    google: 'Noto+Kufi+Arabic:wght@400;500;700',
  },
];

const loadedFonts = new Set<string>();

/** يحمّل خطوط Google المستخدمة مرة واحدة (وإن تعذّر، يبقى البديل النظامي). */
export function ensureFontLoaded(family: string): void {
  if (typeof document === 'undefined') return;
  const preset = FONT_PRESETS.find((entry) => entry.family === family);
  const google = preset?.google;
  if (!google || loadedFonts.has(google)) return;
  loadedFonts.add(google);
  const existing = document.querySelector<HTMLLinkElement>(`link[data-taskflow-font="true"][href*="${google}"]`);
  if (existing) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${google}&display=block`;
  link.dataset.taskflowFont = 'true';
  document.head.appendChild(link);
}

/* ------------------------------- أدوات ألوان ------------------------------- */

function clamp(value: number): number {
  return Math.min(255, Math.max(0, Math.round(value)));
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  let value = match[1];
  if (value.length === 3) value = value.split('').map((char) => char + char).join('');
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

export function rgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/** يمزج لونًا مع الأبيض (ratio = نسبة اللون). */
export function mixWithWhite(hex: string, ratio: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = clamp(rgb.r * ratio + 255 * (1 - ratio));
  const g = clamp(rgb.g * ratio + 255 * (1 - ratio));
  const b = clamp(rgb.b * ratio + 255 * (1 - ratio));
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
}

/** يستخرج لونًا أساسيًا من شعار (أفتح/أكثر تشبّعًا) عبر canvas. */
export async function extractPaletteFromImage(dataUrl: string): Promise<{ primary: string; accent: string }> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('تعذّر قراءة الشعار.'));
    element.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  const size = 64;
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return { primary: '#1f6f6b', accent: '#c9825a' };
  context.drawImage(image, 0, 0, size, size);
  const { data } = context.getImageData(0, 0, size, size);

  // تجميع تقريبي للألوان (bucket 32) مع تجاهل الأبيض/الأسود/الشفاف.
  const buckets = new Map<string, { r: number; g: number; b: number; count: number }>();
  for (let index = 0; index < data.length; index += 4) {
    const alpha = data[index + 3];
    if (alpha < 180) continue;
    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max > 245 && min > 240) continue; // أبيض
    if (max < 25) continue; // أسود
    if (max - min < 22) continue; // رمادي
    const key = `${r >> 5}-${g >> 5}-${b >> 5}`;
    const bucket = buckets.get(key) ?? { r: 0, g: 0, b: 0, count: 0 };
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    bucket.count += 1;
    buckets.set(key, bucket);
  }

  const ranked = [...buckets.values()].sort((a, b) => b.count - a.count);
  const first = ranked[0];
  if (!first) return { primary: '#1f6f6b', accent: '#c9825a' };
  const primary = toHex(first.r / first.count, first.g / first.count, first.b / first.count);
  // لون ثانوي: الأكثر اختلافًا في الصبغة عن الأساسي.
  const primaryHue = hue(first.r / first.count, first.g / first.count, first.b / first.count);
  let accent = toHex(
    Math.min(255, first.r / first.count + 70),
    Math.min(255, first.g / first.count + 40),
    Math.min(255, first.b / first.count + 90),
  );
  let bestDistance = 0;
  for (const bucket of ranked.slice(0, 12)) {
    const r = bucket.r / bucket.count;
    const g = bucket.g / bucket.count;
    const b = bucket.b / bucket.count;
    const distance = Math.abs(hue(r, g, b) - primaryHue);
    if (distance > bestDistance && distance > 25) {
      bestDistance = distance;
      accent = toHex(r, g, b);
    }
  }
  return { primary, accent };
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((value) => clamp(value).toString(16).padStart(2, '0'))
    .join('')}`;
}

function hue(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return 0;
  const delta = max - min;
  let value: number;
  if (max === r) value = ((g - b) / delta) % 6;
  else if (max === g) value = (b - r) / delta + 2;
  else value = (r - g) / delta + 4;
  return (value * 60 + 360) % 360;
}
